import { get, merge, set } from 'lodash';
import moment from 'moment';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getStore } from 'store';
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';
import { getVisibleItems, isVisible } from 'resolvers/getVisibleEntities';
import { isInbound } from 'resolvers/callStateEntities';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc AuthFlowCallerIdentificationMember defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { AuthFlowCallerIdentificationMemberService } from 'services/authFlowCallerIdentificationMember';
 *
 * // create service instance and send/cancel request
 * const authFlowCallerIdentificationMemberService = new AuthFlowCallerIdentificationMemberService();
 * const result = await authFlowCallerIdentificationMemberService.fetchData( '<user-id>' ); // fetch data
 * authFlowCallerIdentificationMemberService.cancel(); // cancel an active request
 */
export class AuthFlowCallerIdentificationMemberService extends HttpService {

    // make request and fetch component data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                        module
                    },
                } ),
            } );

            // component data
            const componentData = get( response, 'body.component', {} );
            const visibility = get( response, 'body.component.visibility', {} );

            // get navigation actions
            const _navActions = get( componentData, 'structure.actionsConfig.navActions', {} );
            const navActions = isVisible( _navActions, visibility ) ? get( _navActions, 'actions', [] ) : [];
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    ...componentData,
                    navActions: getVisibleItems( navActions, visibility ),
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // initiate an outbound call (for member)
    async initiateOutboundCall( { url = {}, data = {}, callerType = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        module,
                        callerType
                    },
                    data: data,
                },
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    ...get( response, 'body.component', {} ),
                    ...get( response, 'body.component.structure', {} )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // start an outbound call (for member)
    async startOutboundCall( { url = {}, data = {}, callerType = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                        module,
                        callerType
                    }
                } )
            } );

            // set the callStartTime to local UTC time
            set( response, 'body.component.data.state.callStartTime', moment.utc().format() );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    callData: get( response, 'body.component.data.state', {} )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // register inbound calller (member)
    async registerInboundCaller( { url = {}, data = {}, callerType = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        module,
                        callerType
                    },
                    data: data,
                },
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: null,
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }
}
